import {useDebounce} from '@/hooks/useDebounceValue'
import {checkNameUnique} from '@/services/ProductComboService/productComboService'
import {useQuery} from '@tanstack/react-query'

export const PRODUCT_COMBO_NAME_UNIQUE_QUERY_KEY = 'product-combo-name-unique'

/**
 * Checks whether a product combo name is unique.
 * The lookup is debounced to avoid firing a request on every keystroke.
 * @param {string} name - Product combo name to validate.
 * @param {string} [productId] - Current combo product id for edit mode comparisons.
 * @returns {boolean} True when the combo name is unique.
 */
export const useIsComboNameUnique = (name, productId) => {
  const debouncedName = useDebounce(name?.trim() ?? '')

  const {data} = useQuery({
    queryKey: [PRODUCT_COMBO_NAME_UNIQUE_QUERY_KEY, debouncedName, productId],
    queryFn: async () => {
      const response = await checkNameUnique(debouncedName)

      if (!response?.unique && productId && response?.conflictingComboId === productId) {
        return true
      }

      return Boolean(response?.unique)
    },
    enabled: debouncedName.length > 0,
    staleTime: 1000 * 10,
  })

  if (!debouncedName) {
    return true
  }

  return data ?? true
}
